<template>
  <div class="cw">
    <ContentSkeleton v-if="loading && !loaded" variant="list" :rows="4" />
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>

    <!-- Por mes: KPIs del año y las barras de los últimos 12 meses. -->
    <template v-else-if="groupBy === 'month'">
      <div class="cw-kpis">
        <div class="cw-kpi">
          <span class="cw-kpi__label">{{ $t("dashboardWidgets.containers.thisMonth") }}</span>
          <span class="cw-kpi__value">{{ fmt(monthStats.thisMonth) }}</span>
        </div>
        <div class="cw-kpi">
          <span class="cw-kpi__label">{{ $t("dashboardWidgets.containers.ytd", { year: currentYear }) }}</span>
          <span class="cw-kpi__value">{{ fmt(monthStats.ytd) }}</span>
        </div>
        <div v-if="monthStats.delta !== null" class="cw-kpi">
          <span class="cw-kpi__label">{{ $t("dashboardWidgets.containers.vsLastYear") }}</span>
          <span class="cw-kpi__value" :class="monthStats.delta >= 0 ? 'text-success' : 'text-danger'">
            {{ monthStats.delta >= 0 ? "+" : "" }}{{ monthStats.delta }}%
          </span>
        </div>
      </div>
      <div class="cw-cols" role="img" :aria-label="$t('dashboardWidgets.items.containers_month.title')">
        <div v-for="c in monthColumns" :key="c.key" class="cw-col" :title="`${c.label}: ${c.value}`">
          <span class="cw-col__value">{{ c.value || "" }}</span>
          <span class="cw-col__bar" :class="{ 'is-current': c.current }" :style="{ height: `${c.pct}%` }"></span>
          <span class="cw-col__label">{{ c.label }}</span>
        </div>
      </div>
    </template>

    <!-- Destino / cliente / shipper: el top del año elegido. -->
    <template v-else>
      <div class="cw-caption">{{ yearLabel }}</div>
      <p v-if="!ranking.length" class="dw-empty">{{ $t("dashboardWidgets.containers.empty") }}</p>
      <div v-for="r in ranking" :key="r.label" class="cw-bar" :title="`${r.label}: ${r.value}`">
        <span class="cw-bar__label">{{ r.label }}</span>
        <span class="cw-bar__track"><span class="cw-bar__fill" :style="{ width: `${r.pct}%` }"></span></span>
        <span class="cw-bar__value">{{ fmt(r.value) }}</span>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Contenedores embarcados (módulo de stock, mismos datos que /containers): por mes o
 * el ranking por destino, cliente o shipper. El detalle completo queda en /containers.
 */
import { wencesApi } from "@/api/wencesApi";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { formatNumberSync } from "@/utils/numberFormatter";

const ENDPOINTS = {
  month: "/api/stock/containers",
  destination: "/api/stock/containers-destination",
  client: "/api/stock/containers-client",
  shipper: "/api/stock/containers-shipper",
};
const LABEL_KEY = { destination: "country", client: "client", shipper: "shipper" };
const MAX_ROWS = 8;

export default {
  name: "ContainersWidget",
  components: { ContentSkeleton },
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return { loading: false, loaded: false, error: false, data: null };
  },
  computed: {
    groupBy() {
      return ENDPOINTS[this.config.groupBy] ? this.config.groupBy : "month";
    },
    currentYear() {
      return new Date().getFullYear();
    },
    /** `current` (default), `previous` o `all`. */
    yearScope() {
      return ["current", "previous", "all"].includes(this.config.year) ? this.config.year : "current";
    },
    yearLabel() {
      if (this.yearScope === "all") return this.$t("dashboardWidgets.containers.allYears");
      return String(this.yearScope === "previous" ? this.currentYear - 1 : this.currentYear);
    },
    rawByKey() {
      const map = new Map();
      ((this.data && this.data.raw) || []).forEach((r) => map.set(`${r.year}-${r.month}`, Number(r.total) || 0));
      return map;
    },
    monthStats() {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth() + 1;
      let ytd = 0;
      let lastYtd = 0;
      for (let i = 1; i <= m; i += 1) {
        ytd += this.rawByKey.get(`${y}-${i}`) || 0;
        lastYtd += this.rawByKey.get(`${y - 1}-${i}`) || 0;
      }
      return {
        thisMonth: this.rawByKey.get(`${y}-${m}`) || 0,
        ytd,
        delta: lastYtd ? Math.round(((ytd - lastYtd) / lastYtd) * 100) : null,
      };
    },
    monthColumns() {
      const out = [];
      const now = new Date();
      const fmt = new Intl.DateTimeFormat(this.$i18n.locale || "es", { month: "short" });
      for (let i = 11; i >= 0; i -= 1) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
        out.push({ key, label: fmt.format(d).replace(".", ""), value: this.rawByKey.get(key) || 0, current: i === 0 });
      }
      const max = Math.max(1, ...out.map((c) => c.value));
      return out.map((c) => ({ ...c, pct: c.value ? Math.max(3, Math.round((c.value / max) * 100)) : 0 }));
    },
    ranking() {
      const grid = (this.data && this.data.grid) || [];
      const labelKey = LABEL_KEY[this.groupBy];
      const year = this.yearScope === "previous" ? this.currentYear - 1 : this.currentYear;
      const rows = grid
        .map((row) => {
          const years = row.years || {};
          const value =
            this.yearScope === "all"
              ? Object.values(years).reduce((s, v) => s + (Number(v) || 0), 0)
              : Number(years[year]) || 0;
          return { label: row[labelKey] || "—", value };
        })
        .filter((r) => r.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, MAX_ROWS);
      const max = Math.max(1, ...rows.map((r) => r.value));
      return rows.map((r) => ({ ...r, pct: Math.max(2, Math.round((r.value / max) * 100)) }));
    },
    menuItems() {
      const items = [];
      if (this.groupBy !== "month") {
        items.push(
          { key: "year:current", label: String(this.currentYear), active: this.yearScope === "current" },
          { key: "year:previous", label: String(this.currentYear - 1), active: this.yearScope === "previous" },
          { key: "year:all", label: this.$tn("dashboardWidgets.containers.allYears"), active: this.yearScope === "all" },
          { divider: true, key: "d1" }
        );
      }
      items.push({ key: "open", label: this.$tn("dashboardWidgets.containers.open"), icon: "external-link-alt" });
      return items;
    },
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(items) {
        this.$emit("menu", items);
      },
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmt(n) {
      return formatNumberSync(n) || String(n || 0);
    },
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const { data } = await wencesApi.get(ENDPOINTS[this.groupBy]);
        this.data = (data && data.data) || null;
        this.loaded = true;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    onMenu(key) {
      if (key.startsWith("year:")) this.$emit("update-config", { year: key.slice(5) });
      else if (key === "open") this.$router.push("/containers");
      else if (key === "refresh") this.load();
    },
  },
};
</script>

<style scoped>
.cw { height: 100%; display: flex; flex-direction: column; gap: 10px; min-height: 0; }
.cw-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(90px, 1fr)); gap: 8px; }
.cw-kpi {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 10px;
  border-radius: 8px;
  background: var(--dw-surface-2);
}
.cw-kpi__label { font-size: 0.72rem; color: var(--dw-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cw-kpi__value { font-size: 1.25rem; font-weight: 700; }
.cw-cols { flex: 1; min-height: 90px; display: flex; align-items: stretch; gap: 4px; }
.cw-col { flex: 1; display: flex; flex-direction: column; justify-content: flex-end; align-items: center; min-width: 0; }
.cw-col__value { font-size: 0.65rem; color: var(--dw-muted); line-height: 1.2; }
.cw-col__bar { width: 100%; max-width: 28px; border-radius: 3px 3px 0 0; background: rgba(var(--bs-primary-rgb), 0.55); }
.cw-col__bar.is-current { background: var(--bs-primary); }
.cw-col__label {
  font-size: 0.65rem;
  color: var(--dw-muted);
  border-top: 1px solid var(--dw-border);
  width: 100%;
  text-align: center;
  padding-top: 2px;
  text-transform: capitalize;
}
.cw-caption { font-size: 0.75rem; color: var(--dw-muted); }
.cw-bar { display: grid; grid-template-columns: minmax(70px, 40%) 1fr auto; align-items: center; gap: 8px; font-size: 0.8rem; }
.cw-bar__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cw-bar__track { height: 10px; border-radius: 5px; background: var(--dw-surface-2); overflow: hidden; }
.cw-bar__fill { display: block; height: 100%; border-radius: 5px; background: var(--bs-primary); }
.cw-bar__value { font-weight: 600; text-align: right; }
</style>
